import React, { useState, useEffect, useCallback, useMemo, memo } from "react";
import { Modal, Form, Input, Select, Switch, Flex } from "antd";
import { UserOutlined, MoreOutlined } from "@ant-design/icons";
import { useSelector, useDispatch } from "react-redux";
import { FetchPlaceholderFields } from "../../../../assets/api/ScriptsService";
import { fetchUserFilters } from "../../../../store/filters";
import { getTemplateComponents, EditTemplate } from "../../../../assets/api/templateService";
import CustomNotification from "../../../../Reuseable/CustomNotification/CustomNotification";
import NormalButton from "../../../../Reuseable/NormalButton/NormalButton";
import "./SmsEditTemplateModal.scss";

const { TextArea } = Input;
const { Option } = Select;

const SmsEditTemplateModal = ({ isOpen, onClose, templateData, onSave }) => {
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const token = useSelector((state) => state.auth.token);
  const { filter_list } = useSelector((state) => state.filters) || {};

  const [bodyText, setBodyText] = useState("");
  const [isBodyExpanded, setIsBodyExpanded] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [languageOptions, setLanguageOptions] = useState([]);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [loadingComponents, setLoadingComponents] = useState(false);

  const [placeholderFields, setPlaceholderFields] = useState([]);
  const [loadingFields, setLoadingFields] = useState(false);
  const [selectedBodyPlaceholder, setSelectedBodyPlaceholder] = useState(null);

  const dynamicPortfoliosOptions = useMemo(() => {
    const portfoliosData = filter_list?.find((item) => item?.name === "portfolios")?.filters || [];
    return portfoliosData.map((d) => ({
      value: d.id || d,
      label: d.name || d.key || d,
    }));
  }, [filter_list]);

  // Fetch API options on mount
  useEffect(() => {
    if (token && isOpen) {
      setLoadingComponents(true);
      getTemplateComponents(token)
        .then((res) => {
          if (res?.data?.data) {
            const data = res.data.data;
            if (Array.isArray(data.languages)) {
              setLanguageOptions(data.languages.map((l) => ({ value: l.code, label: l.label })));
            }
            if (Array.isArray(data.categories)) {
              setCategoryOptions(data.categories.map((c) => ({ value: c.value, label: c.label })));
            }
          }
        })
        .catch((err) => console.error("Error fetching template components:", err))
        .finally(() => setLoadingComponents(false));
    }
  }, [token, isOpen]);

  // Fetch placeholders
  useEffect(() => {
    if (token && isOpen) {
      setLoadingFields(true);
      FetchPlaceholderFields(token)
        .then((res) => {
          let dataArray = [];
          if (res?.data?.data?.field_names && Array.isArray(res.data.data.field_names)) {
            dataArray = res.data.data.field_names;
          } else if (res?.data?.field_names && Array.isArray(res.data.field_names)) {
            dataArray = res.data.field_names;
          } else if (Array.isArray(res?.data)) {
            dataArray = res.data;
          } else if (Array.isArray(res?.data?.data)) {
            dataArray = res.data.data;
          }

          const formatLabel = (str) => {
            if (!str) return "";
            return str
              .split("_")
              .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
              .join(" ");
          };

          const options = dataArray.map((item) => {
            const strVal = typeof item === "string" ? item : item?.name || item?.key || String(item);
            return { value: strVal, label: formatLabel(strVal) };
          });

          setPlaceholderFields(options);
        })
        .catch((err) => console.error("Error fetching placeholders:", err))
        .finally(() => setLoadingFields(false));
    }
  }, [token, isOpen]);

  // Ensure filters are loaded
  useEffect(() => {
    if (isOpen) {
      dispatch(fetchUserFilters());
    }
  }, [dispatch, isOpen]);

  // Prepopulate form when templateData changes
  useEffect(() => {
    if (isOpen && templateData) {
      const bodyComp = templateData.components?.find((c) => c.type === "BODY");

      form.setFieldsValue({
        name: templateData.name,
        sender_id: templateData.sender_id || "",
        dlt_template_id: templateData.dlt_template_id || "",
        language: templateData.language,
        portfolio: templateData.portfolio_id,
        category: templateData.category,
        templateType: "default",
        is_active: templateData.is_active ?? false,
        body: bodyComp?.text || "",
      });

      setBodyText(bodyComp?.text || "");
    } else {
      form.resetFields();
      setBodyText("");
    }
  }, [isOpen, templateData, form]);

  const handleBodyPlaceholderSelect = useCallback((val) => {
    if (!val) return;
    const currentBody = form.getFieldValue("body") || "";
    const placeholderText = `{{xbucketdata.${val}}}`;
    if (currentBody.length + placeholderText.length > 1024) {
      CustomNotification.show("Placeholder cannot be added as it exceeds the 1024 characters limit.", "error");
      setSelectedBodyPlaceholder(null);
      return;
    }
    const newBody = currentBody + placeholderText;
    form.setFieldsValue({ body: newBody });
    setBodyText(newBody);
    setSelectedBodyPlaceholder(null);
  }, [form]);

  const onFinish = useCallback(async (values) => {
    try {
      setSubmitting(true);

      const payload = {};

      // 1. Check Name
      if (values.name !== templateData?.name) {
        payload.name = values.name;
      }

      // 1.1 Check Sender Id
      if (values.sender_id !== templateData?.sender_id) {
        payload.sender_id = values.sender_id;
      }

      // 1.2 Check Dlt Template Id
      if (values.dlt_template_id !== templateData?.dlt_template_id) {
        payload.dlt_template_id = values.dlt_template_id;
      }

      // 2. Check Language
      if (values.language !== templateData?.language) {
        payload.language = values.language;
      }

      // 3. Check Portfolio
      if (values.portfolio && Number(values.portfolio) !== Number(templateData?.portfolio_id)) {
        payload.portfolio_id = Number(values.portfolio);
      }

      // 4. Check Category
      if (values.category !== templateData?.category) {
        payload.category = values.category;
      }

      // 5. Check Status (is_active)
      if (values.is_active !== undefined && !!values.is_active !== !!templateData?.is_active) {
        payload.is_active = !!values.is_active;
      }

      // 6. Check Components
      const bodyComp = templateData?.components?.find((c) => c.type === "BODY");
      const payloadComponents = [];

      // Check Body Change
      const currentBody = values.body || "";
      const originalBody = bodyComp?.text || "";
      
      if (currentBody !== originalBody) {
        payloadComponents.push({
          type: "BODY",
          text: currentBody,
        });
      }

      // Add components to payload if any changed
      if (payloadComponents.length > 0) {
        payload.components = payloadComponents;
      }

      // If no changes at all, notify and close
      if (Object.keys(payload).length === 0) {
        CustomNotification.show("No changes detected", "info");
        onClose();
        return;
      }

      const res = await EditTemplate(templateData.id, token, payload);
      if (res.status === 200 || res.status === 201) {
        CustomNotification.show("Template updated successfully", "success");
        onSave();
        onClose();
      }
    } catch (err) {
      CustomNotification.show(err || "Failed to update template", "error");
    } finally {
      setSubmitting(false);
    }
  }, [token, templateData, onClose, onSave]);

  const toggleBodyExpanded = useCallback(() => {
    setIsBodyExpanded((prev) => !prev);
  }, []);

  return (
    <Modal
      title="Edit SMS Template"
      open={isOpen}
      onCancel={onClose}
      width={1100}
      footer={null}
      centered
      className="smsEditTemplateModalMain"
    >
      <Flex wrap="wrap" gap={24} className="sms-edit-template-flex-layout" style={{ height: "100%" }}>
        {/* Left Section - Form */}
        <div className="sms-left-section">
          <div className="smsEditTemplateFormWrapper">
            <Form form={form} layout="vertical" onFinish={onFinish}>
              <Flex wrap="wrap" gap={16} className="smsFormFieldsGrid">
                <Form.Item
                  label="Name"
                  name="name"
                  rules={[{ required: true, message: "Please enter template name" }]}
                >
                  <Input placeholder="Enter Name" />
                </Form.Item>
                <Form.Item
                  label="Sender Id"
                  name="sender_id"
                  rules={[{ required: true, message: "Please enter sender id" }]}
                >
                  <Input placeholder="Enter Sender Id" />
                </Form.Item>
                <Form.Item
                  label="Dlt Template Id"
                  name="dlt_template_id"
                  rules={[{ required: true, message: "Please enter Dlt template id" }]}
                >
                  <Input placeholder="Enter Dlt Template Id" />
                </Form.Item>
                <Form.Item
                  label="Language"
                  name="language"
                  rules={[{ required: true, message: "Please select language" }]}
                >
                  <Select
                    placeholder="Select Language"
                    options={languageOptions}
                    loading={loadingComponents}
                    allowClear
                  />
                </Form.Item>
                <Form.Item
                  label="Portfolio"
                  name="portfolio"
                  rules={[{ required: true, message: "Please select portfolio" }]}
                >
                  <Select placeholder="Select Portfolio" options={dynamicPortfoliosOptions} allowClear />
                </Form.Item>
                <Form.Item
                  label="Category"
                  name="category"
                  rules={[{ required: true, message: "Please select category" }]}
                >
                  <Select
                    placeholder="Select Category"
                    options={categoryOptions}
                    loading={loadingComponents}
                    allowClear
                  />
                </Form.Item>
                <Form.Item
                  label="Template Type"
                  name="templateType"
                  initialValue="default"
                  rules={[{ required: true, message: "Please select template type" }]}
                >
                  <Select placeholder="Default">
                    <Option value="default">Default</Option>
                  </Select>
                </Form.Item>
                <Form.Item
                  label="Status"
                  name="is_active"
                  valuePropName="checked"
                >
                  <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
                </Form.Item>
              </Flex>

              <div className="smsTextAreasSection">
                {/* Body Section */}
                <div className="sms-custom-body-section" style={{ marginBottom: "16px" }}>
                  <div
                     style={{
                       display: "flex",
                       justifyContent: "space-between",
                       alignItems: "center",
                       marginBottom: "8px",
                     }}
                  >
                    <label className="ant-form-item-required" style={{ color: "#212330", fontWeight: 500 }}>
                      Body
                    </label>
                    <Select
                      showSearch
                      placeholder="Select Placeholder"
                      style={{ width: "150px" }}
                      options={placeholderFields}
                      loading={loadingFields}
                      value={selectedBodyPlaceholder}
                      onChange={handleBodyPlaceholderSelect}
                      allowClear
                      filterOption={(input, option) =>
                        (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                      }
                    />
                  </div>
                  <Form.Item
                    name="body"
                    style={{ marginBottom: 0 }}
                    rules={[{ required: true, message: "Please enter body text" }]}
                  >
                    <TextArea
                      rows={8}
                      maxLength={1024}
                      onChange={(e) => setBodyText(e.target.value)}
                      placeholder="Enter the text for the template in the appropriate language"
                    />
                  </Form.Item>
                  <div style={{ textAlign: "right", fontSize: "12px", color: "#888", marginTop: "4px" }}>
                    {bodyText.length}/1024
                  </div>
                </div>
              </div>

              <Flex wrap="wrap" gap={12} className="smsEditTemplateSubmitAction" style={{ marginTop: "24px", width: "100%" }}>
                <NormalButton
                  type="default"
                  onClick={onClose}
                  className="smsEditTemplateCancelBtn"
                  width="130px"
                >
                  Cancel
                </NormalButton>
                <NormalButton
                  type="primary"
                  htmlType="submit"
                  loading={submitting}
                  className="smsEditTemplateSaveBtn"
                  width="130px"
                >
                  Save Changes
                </NormalButton>
              </Flex>
            </Form>
          </div>
        </div>

        {/* Right Section - SMS Preview */}
        <div className="sms-right-section">
          <div className="smsMockupWrapper">
            <div className="sms-mockup">
              <div className="sms-header">
                <div className="sms-header-left">
                  <div className="sms-avatar">
                    <UserOutlined style={{ color: "#333", fontSize: "18px" }} />
                  </div>
                  <span className="sms-name">Text Message</span>
                </div>
                <MoreOutlined style={{ fontSize: "18px", color: "#333" }} />
              </div>
              <div className="sms-body">
                <div className="sms-date">
                  {new Date().toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", hour: 'numeric', minute: 'numeric' })}
                </div>

                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    width: "100%",
                    maxWidth: "280px",
                    alignSelf: "flex-start",
                  }}
                >
                  <div
                    className="sms-message-bubble"
                    style={{
                      display: bodyText ? "block" : "none",
                      padding: 0,
                    }}
                  >
                    <div style={{ padding: "12px 12px 20px 12px" }}>
                      {bodyText && (
                        <div className="sms-message-body" style={{ whiteSpace: "pre-wrap", fontWeight: "normal" }}>
                          {bodyText.length > 250 ? (
                            <>
                              {isBodyExpanded ? bodyText : `${bodyText.substring(0, 250)}... `}
                              <span 
                                style={{ color: '#027eb5', cursor: 'pointer', fontWeight: 500 }} 
                                onClick={toggleBodyExpanded}
                              >
                                {isBodyExpanded ? ' show less' : ' show more'}
                              </span>
                            </>
                          ) : (
                            bodyText
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Flex>
    </Modal>
  );
};

export default memo(SmsEditTemplateModal);
